import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { FC, useRef, useState } from 'react';
import { Control } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ChevronDownIcon, SearchIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../components/combobox/index';
import Loader from '../../../../components/loader/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useScreenSize } from '../../../../hooks/index';
import { useInfiniteStakeholders } from '../../../../hooks/stakeholder/use-infinite-stakeholders';
import { Stakeholder, stakeholderSingularTypeTitle } from '../../../../types/stakeholder.types';
import { FormSchema } from '../modals.validation';

export type StakeholderValue = {
  fullName: string;
  id: string;
};

export type StakeholdersItemsListProps = {
  control: Control<FormSchema>;
  data: Stakeholder[];
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<
        {
          stakeholders: Stakeholder[];
          totalPages: number;
        },
        unknown
      >,
      Error
    >
  >;
  hasNextPage: boolean;
  isLoading: boolean;
};

const StakeholdersItemsList: FC<StakeholdersItemsListProps> = ({
  data,
  control,
  hasNextPage,
  fetchNextPage,
  isLoading,
}) => {
  const { scrollableTarget } = useScreenSize();
  return (
    <div className="flex w-full flex-col gap-2">
      <InfiniteScroll
        className="flex w-full flex-col gap-2"
        dataLength={data.length || 0}
        hasMore={hasNextPage}
        loader={<Loader />}
        next={fetchNextPage}
        scrollableTarget={scrollableTarget}
      >
        {data.length > 0 ? (
          <>
            {data
              .sort((a, b) => a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase()))
              .map(({ fullName, id, type }, i) => (
                <FormComboboxOption
                  className="w-full"
                  control={control}
                  key={`${fullName}_${i}`}
                  name="stepOne.stakeholder"
                  value={{ fullName, id }}
                >
                  <div className="flex w-full flex-col gap-[1px]">
                    <Tooltip content={fullName}>
                      <span className="truncate text-sm text-gray-700">{fullName}</span>
                    </Tooltip>

                    <span className="truncate text-label-md italic text-gray-500">
                      <AppFormattedMessage id={stakeholderSingularTypeTitle[type]} />
                    </span>
                  </div>
                </FormComboboxOption>
              ))}
          </>
        ) : isLoading ? (
          <span className="py-[6px] text-sm font-normal text-gray-700">
            <AppFormattedMessage id="LOADING" />
          </span>
        ) : (
          <span className="py-[6px] text-sm font-normal text-gray-700">
            <AppFormattedMessage id="NO_SHAREHOLDER_FOUND" />
          </span>
        )}
      </InfiniteScroll>
    </div>
  );
};

export type StakeholderComboboxProps = {
  companyId: string;
  control: Control<FormSchema>;
};

export const StakeholderCombobox: FC<StakeholderComboboxProps> = ({ control, companyId }) => {
  const [search, setSearch] = useState<string>('');

  const { stakeholders, fetchNextPage, hasNextPage, isLoading } = useInfiniteStakeholders({
    companyId,
    search: search || '',
  });

  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <FormCombobox
        className="relative w-full"
        control={control}
        name="stepOne.stakeholder"
        onFocus={() => setSearch('')}
        onSelect={() => {
          setSearch('');
        }}
      >
        <div className="relative">
          <FormComboboxInput
            className={'bg-gray-900'}
            control={control}
            customValue={(value) => (value as StakeholderValue)?.fullName}
            icon={<ChevronDownIcon className="mt-3" />}
            name="stepOne.stakeholder"
            placeholder={<AppFormattedMessage id="SELECT_SHAREHOLDER" />}
            readOnly
          />
          <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
        </div>
        <ComboboxOptions className="w-full overflow-x-hidden">
          <div className="relative flex w-full items-center" onClick={() => ref.current?.focus()}>
            <SearchIcon className="absolute ml-[6px]" />
            <input
              className="w-full rounded border-[2px] border-transparent bg-gray-50 p-[6px] pl-8 text-gray-700 outline-none placeholder:text-gray-400 focus:border-brand-700 focus:bg-white"
              onChange={({ target: { value } }) => setSearch(value)}
              ref={ref}
              value={search}
            />
          </div>
          <StakeholdersItemsList
            control={control}
            data={stakeholders}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
          />
        </ComboboxOptions>
      </FormCombobox>
    </>
  );
};
